import React, { useState, useEffect } from 'react';
import { HashRouter as Router, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import SmartMonitoring from './components/SmartMonitoring';
import DeviceList from './components/DeviceList';
import MaintenanceTaskList from './components/MaintenanceTaskList';
import AISettings from './components/AISettings';
import socketService from './services/socket';
import { LanguageProvider, useLanguage } from './i18n/LanguageContext';
import { RealtimeMonitoringProvider } from './context/RealtimeMonitoringContext';
import './App.css';
import './global-forms.css';

function Footer() {
  const { t } = useLanguage();
  const currentYear = new Date().getFullYear();
  
  return (
    <footer className="footer">
      <div className="footer-content">
        <div className="footer-section">
          <div className="footer-logo">
            <span className="footer-icon">🔥</span>
            <span className="footer-title">{t('login.title')}</span>
          </div>
          <p className="footer-description">
            {t('login.subtitle')}
          </p>
        </div>
      </div>
      
      <div className="footer-bottom">
        <div className="footer-copyright">
          <p>{t('login.copyright')}</p>
          <p className="footer-made">
            {t('login.madeWith')} © {currentYear}
          </p>
        </div>
        <div className="footer-version">
          <span className="version-badge">{t('common.version')}</span>
          <span className="build-info">{t('common.build')}: {new Date().toISOString().slice(0, 10)}</span>
        </div>
      </div>
    </footer>
  );
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const { t } = useLanguage();

  // Заглавие на таба/приложението – винаги FireGuard Live (не „EN“ или друго)
  useEffect(() => {
    document.title = t('login.title');
  }, [t]);

  useEffect(() => {
    // Провери за запазен токен
    const token = localStorage.getItem('fireguard_token');
    const userStr = localStorage.getItem('fireguard_user');

    if (token && userStr && userStr !== 'undefined') {
      try {
        const userData = JSON.parse(userStr);
        setIsAuthenticated(true);
        setUser(userData);

        // Свържи се към Socket.IO сървъра
        socketService.connect();
        
        // Setup real-time notifications
        socketService.on('alert:new', (alert) => {
          console.log('New alert received:', alert);
          // TODO: Show notification toast
        });

        socketService.on('alert:critical', (alert) => {
          console.log('Critical alert received:', alert);
          // TODO: Show critical alert modal
        });

      } catch (error) {
        console.error('Грешка при зареждане на потребителски данни:', error);
        // Изчисти невалидните данни
        localStorage.removeItem('fireguard_token');
        localStorage.removeItem('fireguard_user');
      }
    }

    // Cleanup function
    return () => {
      if (socketService.isConnected()) {
        socketService.disconnect();
      }
    };
  }, []);

  const handleLogin = (userData) => {
    setIsAuthenticated(true);
    setUser(userData);
    
    // Свържи се към Socket.IO сървъра след успешен логин
    socketService.connect();
  };

  const handleLogout = () => {
    localStorage.removeItem('fireguard_token');
    localStorage.removeItem('fireguard_user');
    setIsAuthenticated(false);
    setUser(null);

    // Прекъсни Socket.IO връзката
    socketService.disconnect();

    // Излез от dashboard – изчисти URL до началната страница (login)
    if (typeof window !== 'undefined') {
      window.location.hash = '#/';
    }
  };

  return (
    <Router>
      <RealtimeMonitoringProvider>
        {!isAuthenticated ? (
          <Login onLogin={handleLogin} />
        ) : (
          <div className="app">
            {typeof window !== 'undefined' && (() => {
              const host = (window.location.hostname || '').toLowerCase();
              const onServer = host !== 'localhost' && host !== '127.0.0.1';
              const forced = process.env.REACT_APP_DEMO_MODE === 'true' || /[?&]demo=1/.test(window.location.search || '');
              return onServer || forced;
            })() && (
              <div className="demo-mode-banner" role="status" aria-label="Demo mode">
                🎬 Demo version – data is local (server deployment)
              </div>
            )}
            <Header user={user} onLogout={handleLogout} />
            <main className="main-content">
              <Routes>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/monitoring" element={<SmartMonitoring />} />
                <Route path="/devices" element={<DeviceList />} />
                <Route path="/maintenance" element={<MaintenanceTaskList />} />
                <Route path="/settings" element={<AISettings />} />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </main>
            <Footer />
          </div>
        )}
      </RealtimeMonitoringProvider>
    </Router>
  );
}

function Header({ user, onLogout }) {
  const location = useLocation();
  const { language, changeLanguage, t } = useLanguage();
  const [menuOpen, setMenuOpen] = useState(false);

  const isActive = (path) => {
    return location.pathname === path ? 'active' : '';
  };

  const closeMenu = () => setMenuOpen(false);

  useEffect(() => {
    const onEscape = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    if (menuOpen) {
      document.addEventListener('keydown', onEscape);
      document.body.classList.add('nav-menu-open');
    }
    return () => {
      document.removeEventListener('keydown', onEscape);
      document.body.classList.remove('nav-menu-open');
    };
  }, [menuOpen]);

  return (
    <header className="header">
      {menuOpen && (
        <div className="nav-overlay" onClick={closeMenu} role="button" tabIndex={-1} aria-label="Close menu" />
      )}
      <div className="header-content">
        <div className="header-left">
          <h1 className="logo">🔥 FireGuard Live</h1>
          <button
            type="button"
            className="nav-toggle"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
          >
            <span className="nav-toggle-bar" />
            <span className="nav-toggle-bar" />
            <span className="nav-toggle-bar" />
          </button>
          <nav className={`nav ${menuOpen ? 'nav-open' : ''}`} aria-hidden={!menuOpen}>
            <Link to="/dashboard" className={`nav-link ${isActive('/dashboard')}`} onClick={closeMenu}>
              📊 {t('nav.dashboard')}
            </Link>
            <Link to="/monitoring" className={`nav-link ${isActive('/monitoring')}`} onClick={closeMenu}>
              📈 {t('nav.monitoring')}
            </Link>
            <Link to="/devices" className={`nav-link ${isActive('/devices')}`} onClick={closeMenu}>
              📱 {t('devices.title')}
            </Link>
            <Link to="/maintenance" className={`nav-link ${isActive('/maintenance')}`} onClick={closeMenu}>
              🔧 {t('nav.tasks')}
            </Link>
            <Link to="/settings" className={`nav-link ${isActive('/settings')}`} onClick={closeMenu}>
              ⚙️ {t('nav.settings')}
            </Link>
          </nav>
        </div>

        <div className="header-right">
          <div className="language-switcher" style={{ marginRight: '10px' }}>
            <button
              type="button"
              className={`btn btn-sm ${language === 'bg' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => changeLanguage('bg')}
              style={{ marginRight: '5px', padding: '5px 10px', fontSize: '12px', cursor: 'pointer', minWidth: '36px' }}
              aria-pressed={language === 'bg'}
              aria-label="Bulgarian"
              title="Български"
            >
              BG
            </button>
            <button
              type="button"
              className={`btn btn-sm ${language === 'en' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => changeLanguage('en')}
              style={{ padding: '5px 10px', fontSize: '12px', cursor: 'pointer', minWidth: '36px' }}
              aria-pressed={language === 'en'}
              aria-label="English"
              title="English"
            >
              EN
            </button>
          </div>

          {user && <div className="user-info">
            <span className="user-icon">
              {user.role === 'admin' ? '👑' : user.role === 'inspector' ? '�' : '�👤'}
            </span>
            <span className="user-name">{user && user.username ? user.username : t('roles.user')}</span>
            <span className="user-role">
              {user && (user.role === 'admin' ? t('roles.administrator') : 
               user.role === 'inspector' ? t('roles.inspector') : t('roles.observer'))}
            </span>
          </div>}

          <button className="btn btn-danger btn-logout" onClick={onLogout}>
            🚪 {t('nav.logout')}
          </button>
        </div>
      </div>
    </header>
  );
}

function AppWrapper() {
  return (
    <LanguageProvider>
      <App />
    </LanguageProvider>
  );
}

export default AppWrapper;
